import { create } from 'zustand';
import api from '../utils/api';

/**
 * Trial state store.
 *
 * Tracks user's trial/lock status — fetched from GET /api/trial/lock-state.
 * Used by TrialCountdownBanner, ViewOnlyLockHero, Step5Pricing.
 *
 * Actions:
 *  - fetchLockState() — refresh from backend
 *  - startTrial(fingerprint) — POST /api/trial/start
 *  - recover(planId) — POST /api/trial/recover (returns confirmation URL)
 */
const useTrialStore = create((set, get) => ({
  inTrial: false,
  lockState: 'active',
  trialStartedAt: null,
  trialEndsAt: null,
  daysRemaining: 0,
  lockedAt: null,
  recoveryDiscountPercent: 0,
  loading: false,

  fetchLockState: async () => {
    set({ loading: true });
    try {
      const res = await api.get('/api/trial/lock-state');
      const d = res.data.data;
      set({
        inTrial: d.inTrial,
        lockState: d.lockState,
        trialStartedAt: d.trialStartedAt,
        trialEndsAt: d.trialEndsAt,
        daysRemaining: d.daysRemaining,
        lockedAt: d.lockedAt,
        recoveryDiscountPercent: d.recoveryDiscountPercent || 0,
        loading: false
      });
    } catch (err) {
      console.error('[trialStore.fetchLockState]', err);
      set({ loading: false });
    }
  },

  startTrial: async (deviceFingerprint) => {
    set({ loading: true });
    try {
      const res = await api.post('/api/trial/start', { deviceFingerprint });
      await get().fetchLockState();
      return { success: true, data: res.data.data };
    } catch (err) {
      set({ loading: false });
      return { success: false, error: err.response?.data?.error || 'Failed to start trial' };
    }
  },

  recover: async (planId) => {
    set({ loading: true });
    try {
      const res = await api.post('/api/trial/recover', { planId });
      set({ loading: false });
      return { success: true, data: res.data.data };
    } catch (err) {
      set({ loading: false });
      return { success: false, error: err.response?.data?.error || 'Failed to recover' };
    }
  }
}));

export { useTrialStore };
export default useTrialStore;
